/* eslint-disable react/prop-types */
import { css } from "@emotion/react"
import { useEffect, useState } from "react"
import { Button, Col, Form, Modal } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { getStates, getTypeOfEntity } from "../../../data/enumSlice"
import Loader from "../../../components/Loader"
import { PatternFormat } from "react-number-format"

const componentStyles = css`
    .no-spinners::-webkit-inner-spin-button,
    .no-spinners::-webkit-outer-spin-button {
        -webkit-appearance: none !important;
        margin: 0;
    }

    .no-spinners {
        -moz-appearance: textfield !important;
    }

    .footer-button-container {
        display: flex;
        justify-content: flex-end;
    }

    .footer-error-container {
        display: flex;
        justify-content: flex-end;
        color: red;
        margin-top: 6px;
    }
`
const errorsInitialSchema = {
    name: false,
    provideAdmittedProducerCode: false,
    addressLine1: false,
    city: false,
    zipCode: false,
    typeOfEntity: false,
    state: false
}

const AddEditAgency = ({ show, hide, currentAgency, setCurrentAgency, saveAgency }) => {
    const dispatch = useDispatch()
    const { states, typeOfEntities } = useSelector((state) => state.enums)
    const { fetchStatus } = useSelector((state) => state.agency)

    const [errors, setErrors] = useState(errorsInitialSchema)

    useEffect(() => {
        if (show) {
            setErrors(errorsInitialSchema)
        }
    }, [show])

    useEffect(() => {
        !states?.length && dispatch(getStates())
        !typeOfEntities?.length && dispatch(getTypeOfEntity())
    }, [])

    const handleChange = (event) => {
        const { name, value, type } = event.target
        if (type === "radio") {
            setCurrentAgency({ ...currentAgency, [name]: value === "true" ? true : false })
        } else {
            setCurrentAgency({ ...currentAgency, [name]: value })
        }

        if (errors[name] && !!value) {
            setErrors((preErrors) => ({
                ...preErrors,
                [name]: false
            }))
        }
    }

    const validateForm = (agency) => {
        let clonedErrors = { ...errors }

        for (const [key, value] of Object.entries(clonedErrors)) {
            if (key === "provideAdmittedProducerCode") {
                if (agency?.[key] === true || agency?.[key] === false) {
                    clonedErrors[key] = false
                } else {
                    clonedErrors[key] = true
                }
            } else if (!!agency?.[key]) {
                clonedErrors[key] = false
            } else if (!value && !agency?.[key]) {
                clonedErrors[key] = true
            }
        }
        setErrors(clonedErrors)
        return hasTrueValue(clonedErrors)
    }

    const onSaveAgency = () => {
        if (!validateForm(currentAgency)) {
            saveAgency()
        }
    }

    const hasTrueValue = (obj = {}) => Object.values(obj).some((value) => value === true)

    return (
        <Modal
            show={show}
            onHide={hide}
            size="lg"
            centered
            css={componentStyles}>
            <Modal.Header closeButton>
                <Modal.Title>{currentAgency?.id ? "Edit Agency" : "Add Agency"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading"} />
                <Form>
                    <Form.Group controlId="agencyName">
                        <Form.Label>
                            Agency Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            className={`form-control-sm ${errors.name ? "error" : ""}`}
                            value={currentAgency?.name || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="typeOfEntity"
                        className="mt-2">
                        <Form.Label>
                            Type of Entity <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            name="typeOfEntity"
                            className={`form-select-sm ${errors.typeOfEntity ? "error" : ""}`}
                            value={currentAgency?.typeOfEntity || ""}
                            onChange={handleChange}>
                            <option value={""}>--- Select ---</option>
                            {typeOfEntities?.map((typeOfEntity) => (
                                <option
                                    key={typeOfEntity?.value}
                                    value={typeOfEntity?.value}>
                                    {typeOfEntity?.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group
                        htmlFor="options"
                        className="mt-2">
                        <Form.Label>
                            Were you provided a GAIC Admitted Producer Code? <span className="text-danger">*</span>
                        </Form.Label>
                        <div
                            id="options"
                            className="d-flex">
                            <Form.Check
                                className={`me-3 ${errors.provideAdmittedProducerCode ? "error" : ""}`}
                                type="radio"
                                name="provideAdmittedProducerCode"
                                id="yes"
                                label="Yes"
                                value={true}
                                checked={currentAgency?.provideAdmittedProducerCode === true}
                                onChange={handleChange}
                            />

                            <Form.Check
                                type="radio"
                                className={`me-3 ${errors.provideAdmittedProducerCode ? "error" : ""}`}
                                id="no"
                                name="provideAdmittedProducerCode"
                                label="No"
                                value={false}
                                checked={currentAgency?.provideAdmittedProducerCode === false}
                                onChange={handleChange}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group
                        controlId="addressLine1"
                        className="mt-2">
                        <Form.Label>
                            Company Address Line 1 <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm ${errors.addressLine1 ? "error" : ""}`}
                            name="addressLine1"
                            value={currentAgency?.addressLine1 || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="addressLine2"
                        className="mt-2">
                        <Form.Label>Company Address Line 2</Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm`}
                            name="addressLine2"
                            value={currentAgency?.addressLine2 || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="city"
                        className="mt-2">
                        <Form.Label>
                            Company City <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm ${errors.city ? "error" : ""}`}
                            name="city"
                            value={currentAgency?.city || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="state"
                        className="mt-2">
                        <Form.Label>
                            Company State <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            name="state"
                            className={`form-select-sm ${errors.state ? "error" : ""}`}
                            value={currentAgency?.state || ""}
                            onChange={handleChange}>
                            <option value={""}>--- Select ---</option>
                            {states?.map((state) => (
                                <option
                                    key={state?.value}
                                    value={state?.value}>
                                    {state?.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group
                        controlId="zipCode"
                        className="mt-2">
                        <Form.Label>
                            Zip Code <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm ${errors.zipCode ? "error" : ""}`}
                            name="zipCode"
                            value={currentAgency?.zipCode || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="phoneNumber"
                        className="mt-2">
                        <Form.Label>Main Company Phone Number</Form.Label>
                        <PatternFormat
                            format="(###) ###-####"
                            mask="_"
                            className={`form-control form-control-sm pattern-format`}
                            name="phoneNumber"
                            value={currentAgency?.phoneNumber || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="website"
                        className="mt-2">
                        <Form.Label>Company Web Address</Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm`}
                            name="website"
                            value={currentAgency?.website || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Col sm={6}>
                    <div className="footer-button-container">
                        <Button
                            className="btn-sm me-2"
                            variant="secondary"
                            onClick={hide}>
                            Cancel
                        </Button>
                        <Button
                            className="btn-sm"
                            variant="primary"
                            onClick={onSaveAgency}>
                            Save
                        </Button>
                    </div>
                    {hasTrueValue(errors) && (
                        <div className="footer-error-container">Please fill all required fields</div>
                    )}
                </Col>
            </Modal.Footer>
        </Modal>
    )
}

export default AddEditAgency
